import React, { useEffect, useRef } from 'react';

/**
 * Live audio-level bar meter for a MediaStream. Renders 28 vertical bars
 * driven by an AnalyserNode tapped on the stream — no recorded data, just
 * volume per frequency band. Useful for ops to see at a glance that audio
 * is actually flowing both directions on a call.
 *
 * Props:
 *   stream:  MediaStream | null
 *   color:   bar fill colour (e.g. "#3B82F6")
 *   active:  whether to render (false = static empty bars)
 *   height:  px height of the canvas (default 36)
 */
export function AudioLevelMeter({ stream, color = '#3B82F6', active = true, height = 36 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Size the bitmap to match its CSS size for crispness.
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.scale(dpr, dpr);
    };
    resize();

    // No stream yet → render dim empty bars so the user still sees the meter.
    const drawEmpty = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const barCount = 28;
      const gap = 2;
      const barW = (w - gap * (barCount - 1)) / barCount;
      for (let i = 0; i < barCount; i++) {
        ctx.fillStyle = '#E5E7EB';
        ctx.fillRect(i * (barW + gap), h - 2, barW, 2);
      }
    };

    if (!stream || !active) {
      drawEmpty();
      return () => {};
    }

    // Build the analyser graph.
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128;          // → 64 bins, we'll downsample to 28 bars
    analyser.smoothingTimeConstant = 0.6;
    analyserRef.current = analyser;
    source.connect(analyser);

    const bins = new Uint8Array(analyser.frequencyBinCount);
    const BARS = 28;

    const draw = () => {
      analyser.getByteFrequencyData(bins);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const gap = 2;
      const barW = (w - gap * (BARS - 1)) / BARS;
      // Group the 64 bins into BARS averages for a smooth meter look.
      const groupSize = Math.max(1, Math.floor(bins.length / BARS));
      for (let i = 0; i < BARS; i++) {
        let sum = 0;
        const start = i * groupSize;
        for (let j = 0; j < groupSize && start + j < bins.length; j++) {
          sum += bins[start + j];
        }
        const avg = sum / groupSize;
        const norm = avg / 255; // 0..1
        // Slight gamma + minimum visible height so quiet sounds still show.
        const barH = Math.max(2, Math.pow(norm, 0.65) * h);
        ctx.fillStyle = color;
        ctx.fillRect(i * (barW + gap), h - barH, barW, barH);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { sourceRef.current?.disconnect(); } catch {}
      try { analyserRef.current?.disconnect(); } catch {}
      try { audioCtxRef.current?.close(); } catch {}
    };
  }, [stream, color, active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        width: '100%',
        height: `${height}px`,
        borderRadius: 6,
        background: '#F9FAFB',
      }}
    />
  );
}
